import isEqual from 'lodash/isEqual';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import Auth from '@Services/Auth';
import Breadcrumb from '@Services/Breadcrumb';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import { FEATURES } from '@Constants/rx-ui/constants';
import { dpitems } from '@Constants';
import { updateDialog as updateDialogAction, closeDialog as closeDialogAction } from '@Containers/Dialogs/DialogsActions';
import { stateParams as stateParamsSelector } from '@Selectors/StateParamsSelectors';
import { relativeContextItemsByHitSetKey as relativeContextItemsByHitSetKeySelector } from '@Containers/RelativeContext/RelativeContextSelectors';
import { setActiveResults } from '@Containers/Header/HeaderComponentActions';
import { settings as heatmapSettingsSelector } from '@Pages/Heatmap/selectors';
import { setSettings as setHeatmapSettingsAction } from '@Pages/Heatmap/actions';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import store from '@Store';
import history from '@AppRoutes/history';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import { typeOfHeatmapSettingsDialog, FETCH_SETTINGS, FETCH_SETTINGS_SUCCESS, FETCH_SETTINGS_ERROR } from './constants';
import { trackHeatmap } from './analytics';
import { generateCurrentSettings } from './utils';

export const openDialog = () => (dispatch) => {
  dispatch(
    updateDialogAction({
      id: typeOfHeatmapSettingsDialog,
      dialogType: typeOfHeatmapSettingsDialog,
    })
  );
};

export const closeDialog = () => (dispatch) => {
  dispatch(
    closeDialogAction({
      id: typeOfHeatmapSettingsDialog,
    })
  );
};

export const fetchSettings = () => async (dispatch) => {
  dispatch({ type: FETCH_SETTINGS });
  try {
    const settingsResponse = await MessageBroker.send(RequestUrls.heatmapCreateMatrix);
    dispatch({ type: FETCH_SETTINGS_SUCCESS, payload: settingsResponse });
    return settingsResponse;
  } catch (e) {
    Logger.error(e);
    dispatch({ type: FETCH_SETTINGS_ERROR });
    return undefined;
  }
};

const redirectToHeatmap = () => (dispatch, getState) => {
  const stateParams = stateParamsSelector(getState()).toJS();
  const { context, searchId, limitOrExclude, hitSetKey } = stateParams;
  const relativeContextsImmutable = relativeContextItemsByHitSetKeySelector(getState(), { hitSetKey });
  const dpItemImmutable = relativeContextsImmutable && relativeContextsImmutable.get(dpitems);
  const params = {
    ...(dpItemImmutable && dpItemImmutable.toJS()),
    fromContext: context,
    searchId,
    sortedMatrixName: null,
    limitOrExclude,
    trackAACustom: true,
  };
  const breadcrumb = {
    ...stateParams,
    heatmapResultName: dpItemImmutable.get('resultName'),
    fromContext: context,
    isHeatmapShown: true,
    sortedMatrixName: null,
  };

  trackHeatmap(relativeContextsImmutable, { hitSetKey });
  Breadcrumb.updateBreadcrumbs(breadcrumb);
  dispatch(setActiveResults(true));
  const heatmapPath = generateHeatmapPath(params);

  history.push({
    pathname: heatmapPath,
    state: params,
  });
};

const shouldResetSortedMatrixName = (heatmapSettings, currentSettings) => {
  const mapSettings = ({ col, row, cellOperation }) => ({ col, row, cellOperation });
  return !isEqual(mapSettings(heatmapSettings), mapSettings(currentSettings));
};

const shouldReloadState = (heatmapSettings, currentSettings) => {
  const mapSettings = ({ col, row, cellOperation, showSubstances }) => ({ col, row, cellOperation, showSubstances });
  return !isEqual(mapSettings(heatmapSettings), mapSettings(currentSettings));
};

export const applySettings = (currentSettings) => (dispatch, getState) => {
  const heatmapSettings = heatmapSettingsSelector(getState());

  dispatch(setHeatmapSettingsAction(currentSettings));

  if (history.location.pathname.indexOf('heatmap') === -1) {
    // If from results - redirect to heatmap
    dispatch(redirectToHeatmap());
  } else if (shouldResetSortedMatrixName(heatmapSettings, currentSettings)) {
    // Reset sorted matrix name, reload state
    const params = getCombinedRouteParams(store.getState());
    params.sortedMatrixName = null;
    params.index = null;
    params.isRow = null;
    params.ascending = null;
    const pathName = generateHeatmapPath(params);
    history.push({
      pathname: pathName,
      state: params,
    });
  } else if (shouldReloadState(heatmapSettings, currentSettings)) {
    // Just reload state
    const params = getCombinedRouteParams(store.getState());
    const pathName = generateResultsPath(params);
    history.push({
      pathname: pathName,
      state: params,
    });
  }

  dispatch(closeDialog());
};

// Case when Bioactivity visualization settings not enabled - use default
export const fetchAndApplyDefaultSettings = withGlobalSpinnerActionEnhancer(() => async (dispatch, getState) => {
  const heatmapSettings = heatmapSettingsSelector(getState());
  if (heatmapSettings) {
    dispatch(redirectToHeatmap());
  } else {
    const settingsResponse = await dispatch(fetchSettings());
    const defaultSettings = generateCurrentSettings(settingsResponse);
    dispatch(applySettings(defaultSettings));
  }
});

export const onHeatmapButtonClick = () => async (dispatch, getState) => {
  const heatmapSettingsFeatureEnabled = Auth.isFeatureEnabled(FEATURES.enableBioactivityVisualizationSettings);
  const heatmapSettings = heatmapSettingsSelector(getState());

  if (!heatmapSettingsFeatureEnabled) {
    await dispatch(fetchAndApplyDefaultSettings());
    return;
  }

  if (heatmapSettings && heatmapSettings.showPopup === false) {
    dispatch(redirectToHeatmap());
  } else {
    dispatch(openDialog());
  }
};
